// Annual Vendor Audit — the staff client. Mirrors PhoenixCapital.Core's
// VendorAuditDtos exactly. Reads are gated Vendors.Read server-side, every
// write Vendors.Manage; there is no new permission.
//
// EVERY WRITE RETURNS THE FULL DETAIL DTO, so no caller ever needs a
// follow-up GET — the mutation's own response is the new state of the record.

import { api, API_BASE } from "@/api/client";

/** The AUDIT's lifecycle status — never the vendor's own registry status.
 *  Two different things in this module are called "status", so neither type
 *  is named just that. */
export type VendorAuditStatus =
  | "draft"
  | "in-vendor-review"
  | "awaiting-final-assessment"
  | "completed"
  | "superseded";

export type VendorAuditDetermination =
  | "competitive"
  | "not-competitive"
  | "inconclusive";

export type VendorAuditRecommendation = "retain" | "renegotiate" | "replace";

/** Which side of the thread an entry came from. A contract, not a label —
 *  the vendor portal mirrors these strings. */
export type VendorAuditCommentRole = "phoenix" | "vendor";

/** Why a vendor fell out of scope. Two distinct facts with two distinct
 *  fixes — invite them, versus re-enable the account they already have — so
 *  they are never collapsed into one string. */
export type VendorAuditExclusionReason =
  | "no portal account"
  | "portal account unusable";

export interface VendorAuditSummaryDto {
  id: string;
  vendorId: string;
  vendorName: string;
  auditYear: number;
  status: VendorAuditStatus;
  releasedAt: string | null;
  reviewClosesAt: string | null;
  reviewClosedAt: string | null;
  reviewClosedEarly: boolean;
  /** Whole days left in the vendor's comment window, floored at 0. A window
   *  past its close date reads 0, never a negative — "overdue" is derived by
   *  the caller from `reviewClosesAt` so the two facts stay separable. */
  daysRemaining: number | null;
  finalDetermination: VendorAuditDetermination | null;
  finalRecommendation: VendorAuditRecommendation | null;
  completedAt: string | null;
  supersedesId: string | null;
  reportDocumentId: string | null;
  reportFiledAt: string | null;
  quoteCount: number;
  commentCount: number;
  createdAt: string;
}

/** One competitor approached. `declined` is a RESULT, not a blank row — it is
 *  the evidence behind an inconclusive determination. */
export interface VendorAuditQuoteDto {
  id: string;
  competitorName: string;
  contactName: string | null;
  contactMethod: string | null;
  contactedOn: string | null;
  quotedTerms: string | null;
  servicesOffered: string | null;
  declined: boolean;
  sortOrder: number;
}

export interface VendorAuditCommentDto {
  id: string;
  authorRole: VendorAuditCommentRole;
  authorName: string | null;
  body: string;
  createdAt: string;
}

export interface VendorAuditDetailDto {
  id: string;
  vendorId: string;
  vendorName: string;
  auditYear: number;
  status: VendorAuditStatus;
  releasedAt: string | null;
  reviewClosesAt: string | null;
  reviewClosedAt: string | null;
  reviewClosedEarly: boolean;
  daysRemaining: number | null;
  phoenixPricingSummary: string | null;
  phoenixServicesSummary: string | null;
  preliminaryFinding: string | null;
  finalDetermination: VendorAuditDetermination | null;
  finalRecommendation: VendorAuditRecommendation | null;
  finalNarrative: string | null;
  completedAt: string | null;
  completedByName: string | null;
  supersedesId: string | null;
  reportDocumentId: string | null;
  reportFiledAt: string | null;
  createdAt: string;
  quotes: VendorAuditQuoteDto[];
  comments: VendorAuditCommentDto[];
}

/** A vendor that owes an audit for the year. `auditStatus` is null when none
 *  has been started — a superseded-only vendor reads as not started, which is
 *  what it is. */
export interface VendorAuditScopeRowDto {
  vendorId: string;
  vendorName: string;
  /** The VENDOR's own registry status. */
  vendorStatus: string;
  /** The AUDIT's lifecycle status, or null when none exists. */
  auditStatus: VendorAuditStatus | null;
  auditId: string | null;
}

export interface VendorAuditExcludedRowDto {
  vendorId: string;
  vendorName: string;
  vendorStatus: string;
  reason: VendorAuditExclusionReason;
}

/**
 * Who owes an audit this year, and how far through the year we are.
 *
 * `inScopeCount` and `completedCount` together read "14 of 16". The
 * denominator is on the wire deliberately: a surface that branches only on
 * whether a list is empty cannot tell "nothing is owed" from "everything owed
 * is done", and `VendorReportComplianceService` shipped exactly that bug.
 */
export interface VendorAuditScopeDto {
  auditYear: number;
  inScopeCount: number;
  completedCount: number;
  inScope: VendorAuditScopeRowDto[];
  /** Vendors that would otherwise owe an audit but cannot be given one.
   *  Reported rather than silently netted out of the denominator: "we audited
   *  everyone we could" and "we audited everyone" are different claims. */
  excludedNoPortalAccount: VendorAuditExcludedRowDto[];
}

export interface VendorAuditListParams {
  year?: number;
  vendorId?: string;
  status?: VendorAuditStatus;
}

/** Null means unchanged, empty string means clear (FIND-12) — so a caller
 *  hydrates every field and sends all three. */
export interface UpdateVendorAuditDraftRequest {
  phoenixPricingSummary?: string | null;
  phoenixServicesSummary?: string | null;
  preliminaryFinding?: string | null;
}

export interface AddVendorAuditQuoteRequest {
  competitorName: string;
  contactName?: string | null;
  contactMethod?: string | null;
  contactedOn?: string | null;
  quotedTerms?: string | null;
  servicesOffered?: string | null;
  declined: boolean;
  sortOrder?: number | null;
}

/** Same null-means-unchanged rule, except `declined` / `sortOrder`, which are
 *  only applied when supplied. */
export type UpdateVendorAuditQuoteRequest = Partial<AddVendorAuditQuoteRequest>;

export interface CompleteVendorAuditRequest {
  determination: VendorAuditDetermination;
  recommendation: VendorAuditRecommendation;
  narrative?: string | null;
}

/** The vendor's comment window. Mirrors `VendorAuditReviewWindow` so the
 *  client cannot offer a value the server will refuse. */
export const VENDOR_AUDIT_REVIEW_WINDOW = {
  defaultDays: 30,
  minDays: 1,
  maxDays: 180,
} as const;

const ROOT = "/portfolio-admin/vendor-audits";

export const vendorAuditsApi = {
  list: (params?: VendorAuditListParams) =>
    api.get<VendorAuditSummaryDto[]>(ROOT, {
      year: params?.year,
      vendorId: params?.vendorId,
      status: params?.status,
    }),
  /** Omit `year` and the server resolves the PHOENIX calendar year — the
   *  business is in Arizona, so on 1 January at 02:00 UTC the answer is still
   *  last year. Never send a client-derived year as the default. */
  scope: (year?: number) =>
    api.get<VendorAuditScopeDto>(`${ROOT}/scope`, { year }),
  get: (auditId: string) =>
    api.get<VendorAuditDetailDto>(`${ROOT}/${auditId}`),
  /** Every audit ever run for one vendor — the record's history strip. */
  listForVendor: (vendorId: string) =>
    api.get<VendorAuditSummaryDto[]>(`${ROOT}/vendor/${vendorId}`),

  create: (body: { vendorId: string; auditYear: number }) =>
    api.post<VendorAuditDetailDto>(ROOT, body),
  updateDraft: (auditId: string, body: UpdateVendorAuditDraftRequest) =>
    api.put<VendorAuditDetailDto>(`${ROOT}/${auditId}/draft`, body),

  addQuote: (auditId: string, body: AddVendorAuditQuoteRequest) =>
    api.post<VendorAuditDetailDto>(`${ROOT}/${auditId}/quotes`, body),
  updateQuote: (
    auditId: string,
    quoteId: string,
    body: UpdateVendorAuditQuoteRequest,
  ) =>
    api.put<VendorAuditDetailDto>(`${ROOT}/${auditId}/quotes/${quoteId}`, body),
  removeQuote: (auditId: string, quoteId: string) =>
    api.delete<VendorAuditDetailDto>(`${ROOT}/${auditId}/quotes/${quoteId}`),

  release: (auditId: string, body: { reviewWindowDays?: number }) =>
    api.post<VendorAuditDetailDto>(`${ROOT}/${auditId}/release`, body),
  closeReview: (auditId: string) =>
    api.post<VendorAuditDetailDto>(`${ROOT}/${auditId}/close-review`),
  complete: (auditId: string, body: CompleteVendorAuditRequest) =>
    api.post<VendorAuditDetailDto>(`${ROOT}/${auditId}/complete`, body),
  /** Retires a completed audit and returns its replacement DRAFT. The only
   *  way to change a completed audit — the original is never edited. */
  supersede: (auditId: string) =>
    api.post<VendorAuditDetailDto>(`${ROOT}/${auditId}/supersede`),
  addComment: (auditId: string, body: { body: string }) =>
    api.post<VendorAuditDetailDto>(`${ROOT}/${auditId}/comments`, body),

  /** Raw PDF endpoints (not enveloped) — used as an anchor href, the
   *  loanReportsApi.previewUrl convention. Cookie auth carries, so no fetch
   *  is needed. Both are Vendors.Read; the report is INTERNAL (D9) and the
   *  vendor never receives a copy — their access is the portal's own view. */
  reportPreviewUrl: (auditId: string) =>
    `${API_BASE}${ROOT}/${auditId}/report/preview`,
  reportDownloadUrl: (auditId: string) =>
    `${API_BASE}${ROOT}/${auditId}/report/download`,
};
